"use client"

import React from "react"
import { SIWEProvider, SIWEConfig, ConnectKitProvider } from "connectkit"
import { generateNonce, verifyNonce, getWalletSession } from "@/lib/api/users"
import { createSiweMessage, parseSiweMessage } from "viem/siwe"
import { toast } from "sonner"

export default function ConnectKitSIWEProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const siweConfig: SIWEConfig = {
    getNonce: async () => {
      const nonce = await generateNonce()
      if (!nonce) {
        throw new Error("Unable to generate nonce")
      }
      return nonce
    },

    createMessage: ({ nonce, address, chainId }) => {
      return createSiweMessage({
        version: "1",
        domain: window.location.host,
        uri: window.location.origin,
        address,
        chainId,
        nonce,
        statement: "Sign in with Ethereum to the app.",
      })
    },

    verifyMessage: async ({ message, signature }) => {
      const address = parseSiweMessage(message).address

      if (!address) {
        console.error("Address not found in message")
        return false
      }

      try {
        const verified = await verifyNonce({
          wallet_address: address,
          signature,
          message,
        })

        return "id" in verified ? true : false
      } catch (error: any) {
        // Try to extract the message from the error.
        if (error && "data" in error && "msg" in error.data) {
          const errMsg = error.data.msg
          toast.error(errMsg)

          console.error("Error from connectkit", { errMsg, error })
        } else {
          console.error("Failed to parse connectkit error message: ", error)
        }

        // Return false to indicate verification failed
        return false
      }
    },

    getSession: async () => {
      const response = await getWalletSession()
      if (!response) return null
      return response
    },

    signOut: async () => {
      try {
        // Clear any session info stored locally
        sessionStorage.removeItem("siwe")
        localStorage.removeItem("wagmi.connected")
        localStorage.removeItem("connektkitSiweSession")

        // You can call your backend logout endpoint here if needed
        // await fetch('/api/logout', { method: 'POST' })

        return true
      } catch (error) {
        console.error("Error during sign out:", error)
        return false
      }
    },

    // Optional configurations
    enabled: true,
    nonceRefetchInterval: 300000, // 5 minutes
    sessionRefetchInterval: 300000, // 5 minutes
    signOutOnDisconnect: true,
    signOutOnAccountChange: true,
    signOutOnNetworkChange: false,
  }

  return (
    <SIWEProvider {...siweConfig}>
      <ConnectKitProvider>{children}</ConnectKitProvider>
    </SIWEProvider>
  )
}
